import axios from "axios";
import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";
import { AwsDataAccess, AzureResponseProps, GcpResponseProps, K8sResponseProps, OnPremResponseProps, webURLResponseProps } from "../resourceRequestInterface";
import binaryApi from "../../../../utils/binaryApi";
import moment from "moment";

const inflightApiRequests = new Map<string, Promise<unknown>>();

function dedupeApiRequest<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = inflightApiRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = factory().finally(() => {
    inflightApiRequests.delete(key);
  });
  inflightApiRequests.set(key, promise);
  return promise;
}

const parseReportErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error) || !error.response?.data) {
    return undefined;
  }

  const data = error.response.data;
  if (typeof data === "string") {
    try {
      const json = JSON.parse(data);
      return json.message || json.error;
    } catch {
      return data;
    }
  }

  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder().decode(data);
      const json = JSON.parse(text);
      return json.message || json.error || text;
    } catch {
      return undefined;
    }
  }

  if (typeof data === "object" && data !== null && "message" in data) {
    return String((data as { message?: string }).message);
  }

  return undefined;
};
export const getAdminNotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {
  try {
    const params: Record<string, any> = {
      tenantName,
      computerName,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status) params.status = status;
    if (searchTerm) params.search = searchTerm;
    if (email) params.email = email;

    if (computerName.length === 0) params.computerName = null;



    if (role === "TENANT_USER") {
      params.computerName = null;

    }
    params.type = "onprem"
    let url = `${NGROK}/api/access/notifications/count`;
    const requestKey = `${url}:${JSON.stringify(params)}`;
    const response = await dedupeApiRequest(requestKey, () =>
      apiClient({
        method: "GET",
        url,
        params,
      }),
    );

    return response || 0;
  } catch (error) {
    return 0;
  }
};




export const getAdminNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<OnPremResponseProps[]> => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      computerName,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status) params.status = status;
    if (searchTerm) params.search = searchTerm;
    if (email) params.email = email;

    if (computerName.length === 0) params.computerName = null;

    let url = `${NGROK}/api/access/admin-notifications`;

    if (role === "TENANT_USER") {
      params.computerName = null;
      url = `${NGROK}/api/access/user-notifications-by-email`;
    }

    const requestKey = `${url}:${JSON.stringify(params)}`;
    const response = await dedupeApiRequest(requestKey, () =>
      apiClient<OnPremResponseProps[]>({
        method: "GET",
        url,
        params,
      }),
    );

    return response || [];
  } catch (error) {
    console.error("getAdminNotificationAPI error:", error);
    return [];  // Return empty array in case of error
  }
};

export const getWebURLNotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;

    if (role === "TENANT_USER") params.email = email;

    params.type = "weburl"
    let url = `${NGROK}/api/access/notifications/count`;


    const response = await apiClient({
      method: "GET",
      url,
      params,
    });

    return response || 0;  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get WebURL error:", error);
    return 0;  // Return empty array in case of error
  }
};

export const getWebURLNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<webURLResponseProps[]> => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") params.email = email;

    let url = `${NGROK}/api/websites/weburl-request-access`;


    const response = await apiClient<webURLResponseProps[]>({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || [];  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get WebURL error:", error);
    return [];  // Return empty array in case of error
  }
};


export const getAzureNotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;

    if (role === "TENANT_USER") {
      params.email = email
    } else {
      params.email = null;
    }
    params.type = "azure"
    let url = `${NGROK}/api/access/notifications/count`;

    const response = await apiClient({
      method: "GET",
      url,
      params,
    });

    return response || 0;
  } catch (error) {
    console.error("Get WebURL error:", error);
    return [];
  }
};

export const getAzureNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<AzureResponseProps[]> => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") {
      params.email = email
    } else {
      params.email = null;
    }

    let url = `${NGROK}/api/azureResources/v1/requests/all`;

    console.log(params)

    const response = await apiClient<AzureResponseProps[]>({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || [];  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get WebURL error:", error);
    return [];  // Return empty array in case of error
  }
};



export const getGCPNotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
      projectId: "whiteswan-vm-allocation"
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") {
      params.email = email
    } else {
      params.email = null;
    }
    params.type = "gcp"
    let url = `${NGROK}/api/access/notifications/count`;


    const response = await apiClient({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || 0;  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get WebURL error:", error);
    return 0;  // Return empty array in case of error
  }
};

export const getGCPNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<GcpResponseProps[]> => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
      projectId: "whiteswan-vm-allocation"
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") {
      params.email = email;
    } else {
      params.email = null;
    }

    let url = `${NGROK}/api/gcp/resource/gcp-request-access`;


    const response = await apiClient<GcpResponseProps[]>({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || [];  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get WebURL error:", error);
    return [];  // Return empty array in case of error
  }
};



export const getK8NotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") {
      params.email = email;
    } else {
      params.email = null;
    }
    params.type = "k8"
    let url = `${NGROK}/api/access/notifications/count`;

    const response = await apiClient({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || 0;  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get K8 error:", error);
    return 0;  // Return empty array in case of error
  }
};

export const getK8sNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<K8sResponseProps[]> => {  // Return an array
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;


    if (role === "TENANT_USER") {
      params.email = email;
    } else {
      params.email = null;
    }

    let url = `${NGROK}/api/k8-resources/v1/requests/all`;

    const response = await apiClient<K8sResponseProps[]>({  // Fetch array
      method: "GET",
      url,
      params,
    });

    return response || [];  // Ensure it returns an array, even if response is undefined
  } catch (error) {
    console.error("Get K8 error:", error);
    return [];  // Return empty array in case of error
  }
};

export const updateRequest = async (tenantName: string, payload: any) => {
  let url = `${NGROK}/api/websites/weburl-request-access?tenantName=${tenantName}`;
  const response = await apiClient({  // Fetch array
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const revokeRequest = async (type: string, accessid: string | number) => {
  let url = `${NGROK}/api/access/revoke/request?type=${type}&accessid=${accessid}`
  const response = await apiClient({
    method: "POST",
    url
  });
  return response;

}

export const checkNebulaStatus = async (computerId: any) => {
  let url = `${NGROK}/api/computers/${computerId}/nebula-status`;
  try {
    const res = await apiClient({
      method: "GET",
      url,
    });
    return res?.enabled;
  } catch (error) {
    console.error("Error checking Nebula status", error);
    return true;
  }
};

export const changeStatus = async (status: string, accessid: number) => {
  let url = `${NGROK}/api/access/update`
  let payload = {
    accessId: accessid,
    requestStatus: status
  }
  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const updateWhiteswanAccess = async (payload: any, tenantName: string) => {

  let url = `${NGROK}/api/${tenantName}/whiteswan-access`

  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const updateGroupTypeStatus = async (payload: any, tenantName: string, computerId: number) => {

  let url = `${NGROK}/api/${tenantName}/computer-user-groups/membership?computerId=${computerId}`

  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const updateFolder = async (payload: any, tenantName: string) => {

  let url = `${NGROK}/api/${tenantName}/computer-user/folders/timer`

  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const updateApp = async (payload: any, tenantName: string) => {

  let url = `${NGROK}/api/${tenantName}/computer-user/publishers/apps/timer`

  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}

export const updateUrl = async (payload: any, tenantName: string) => {

  let url = `${NGROK}/api/${tenantName}/computer-user/url-control/timer`

  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}


export const downloadOnPremRequestReport = async (
  type: string,
  tenantName: string,
  computerName: string,
  role: string,
  startDate?: string,
  endDate?: string,
  searchTerm?: string,
  email?: string,
  status?: string
) => {
  if (!tenantName?.trim()) {
    throw new Error("Tenant name is required to generate the report");
  }

  try {
    const normalizedType = type.toLowerCase();
    let reportType = normalizedType === "k8s" ? "k8" : type;
    if (normalizedType === "weburl") {
      reportType = "webURL";
    }

    const params: Record<string, string> = {
      tenantName: tenantName.trim(),
      type: reportType,
    };

    if (role) params.role = role;
    if (computerName?.length > 0) params.computerName = computerName;
    if (startDate) params.startDate = moment(startDate).format("YYYY-MM-DD");
    if (endDate) params.endDate = moment(endDate).format("YYYY-MM-DD");
    if (status && status.toLowerCase() !== "all") {
      const normalizedStatus = status.trim().toUpperCase();
      params.status = normalizedStatus === "DECLINED" ? "DECLINE" : normalizedStatus;
    }
    if (searchTerm?.trim()) params.search = searchTerm.trim();
    if (email && !email.startsWith("wsadmin")) params.email = email;

    const url = `${NGROK}/api/report/generate/onprem-report`;

    const response = await binaryApi.get(url, { params });

    const data = response.data as ArrayBuffer | Blob;
    const dataSize =
      data instanceof Blob ? data.size : (data as ArrayBuffer)?.byteLength ?? 0;
    const contentType = (response.headers["content-type"] || "").toLowerCase();

    if (contentType.includes("application/json") || dataSize === 0) {
      throw new Error("Report generation failed on the server");
    }

    const blob =
      data instanceof Blob
        ? data
        : new Blob([data], { type: "application/pdf" });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${normalizedType === "k8s" ? "k8" : normalizedType}-access-report.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);

  } catch (error) {
    console.error("Failed to download report", error);
    throw new Error(parseReportErrorMessage(error) || "Failed to download report");
  }
};



export const changeAzureStatus = async (status: string, accessid: number, tenantName: string) => {
  let url = `${NGROK}/api/azureResources/v1/requests/process?tenantName=${tenantName}&id=${accessid}&status=${status}`
  let payload = {
    accessId: accessid,
    requestStatus: status
  }
  const response = await apiClient({
    method: "PATCH",
    url,
  });
  return response;
}

export const changeK8Status = async (status: string, accessid: string, tenantName: string) => {
  let url = `${NGROK}/api/k8-resources/v1/requests/process?uuid=${accessid}&status=${status}`

  const response = await apiClient({
    method: "PATCH",
    url
  });
  return response;
}

export const changeGCPStatus = async (status: string, accessid: number, tenantName: string, projectId: string, notification: GcpResponseProps) => {
  let url = `${NGROK}/api/gcp/resource/gcp-request-access?tenantName=${tenantName}&projectId=${projectId}`;
  let payload = {
    accessId: accessid,
    requestStatus: status,
    accessType: notification.accessType,
    gcpUseremail: notification.gcpUserEmail,
    gcpResourceType: notification?.gcpResourceType,
  }
  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}


export const getAWSNotificationCountAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
) => {
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;

    if (role === "TENANT_USER") {
      params.email = email
    } else {
      params.email = null;
    }

    params.type = "aws"
    let url = `${NGROK}/api/access/notifications/count`;

    const response = await apiClient({
      method: "GET",
      url,
      params,
    });

    return response || 0;
  } catch (error) {
    console.error("getAdminNotificationAPI error:", error);
    return [];
  }
};

export const getAWSNotificationAPI = async (
  computerName: string,
  searchTerm: string,
  status: string,
  startDate: string,
  endDate: string,
  tenantName: string,
  page: number,
  email: string,
  role: string
): Promise<AwsDataAccess[]> => {
  try {
    const params: Record<string, any> = {
      tenantName,
      page: page + 1,
      limit: 15,
      role,
    };

    if (startDate || startDate.length > 0) params.startDate = startDate;
    if (endDate || endDate.length > 0) params.endDate = endDate;
    if (status !== "ALL") params.status = status;
    if (searchTerm) params.search = searchTerm;

    if (role === "TENANT_USER") {
      params.email = email;
    } else {
      params.email = null;
    }

    let url = `${NGROK}/api/aws-request-access`;

    const response = await apiClient<AwsDataAccess[]>({
      method: "GET",
      url,
      params,
    });

    return response || [];
  } catch (error) {
    console.error("getAdminNotificationAPI error:", error);
    return [];
  }
};

export const changeAWSStatus = async (tenantName: string, payload: any) => {
  let url = `${NGROK}/api/aws-request-access?tenantName=${tenantName}`;
  const response = await apiClient({
    method: "PUT",
    url,
    data: payload
  });
  return response;
}
